import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useCreateProductionDeclarationCorrectionRequest } from '@/services/apis/mutations/production-declaration/use-create-production-declaration-correction-request';
import type {
  CorrectionRequest,
  CorrectionRequestEntityType,
} from '@/types/core/purchase-order/correction-request';

type UseProductionDeclarationCorrectionRequestCommentCreateParams = {
  commentsQueryKey: QueryKey;
  commodityOriginId: string;
  entityType: CorrectionRequestEntityType;
  materialId: string;
  productionDeclarationId: string;
};

type ApiErrorResponse = {
  detail?: string;
  errors?: {
    comment?: string[];
  };
  message?: string;
};

export function useProductionDeclarationCorrectionRequestCommentCreate({
  commentsQueryKey,
  commodityOriginId,
  entityType,
  materialId,
  productionDeclarationId,
}: UseProductionDeclarationCorrectionRequestCommentCreateParams) {
  const [comment, setComment] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation('common', {
    useSuspense: false,
  });
  const { isPending: isCreatingComment, mutate: createCorrectionRequest } =
    useCreateProductionDeclarationCorrectionRequest();
  const handleCreateCancel = () => {
    setComment('');
    setIsFormOpen(false);
  };

  const handleCreateSubmit = () => {
    createCorrectionRequest(
      {
        productionDeclarationId,
        materialId,
        commodityOriginId,
        entityType,
        comment,
      },
      {
        onSuccess: (createdComment) => {
          queryClient.setQueryData<CorrectionRequest[]>(
            commentsQueryKey,
            (currentComments = []) => [createdComment, ...currentComments],
          );
          setComment('');
          setIsFormOpen(false);
          toast.success(t('materialCommodityOrigins.comments.createSuccess'));
        },
        onError: (error) => {
          const description = axios.isAxiosError<ApiErrorResponse>(error)
            ? (error.response?.data?.message ??
              error.response?.data?.detail ??
              error.response?.data?.errors?.comment?.at(0) ??
              t('materialCommodityOrigins.comments.createError'))
            : t('materialCommodityOrigins.comments.createError');

          toast.error(tCommon('error'), {
            description,
          });
        },
      },
    );
  };

  return {
    comment,
    handleCreateCancel,
    handleCreateSubmit,
    isCreatingComment,
    isFormOpen,
    openForm: () => setIsFormOpen(true),
    setComment,
  };
}
